import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mic, MessageSquare, MicOff, Square } from "lucide-react";
import { VoiceAgentAvatar } from "@/components/VoiceAgent";
import { Button } from "@/components/ui/button";
import { useAgentPageByUrlNameFull } from "@/hooks/agent/useAgentPage";
import { useAudioSessionControls } from "@/hooks/voice/useAudioSessionControls";
import { useChannelRequests } from "@/hooks/voice/useChannelRequests";
import { useTextChatSession } from "@/hooks/voice/useTextChatSession";
import { useConversationImageMediaSaver } from "@/hooks/voice/useConversationImageMediaSaver";
import { useImageAttachments } from "@/hooks/voice/useImageAttachments";
import { useVoiceAgentConversation } from "@/hooks/voice/useVoiceAgentConversation";
import { useHumanHandoff } from "@/hooks/voice/useHumanHandoff";
import { useInConversationCapture } from "@/hooks/voice/useInConversationCapture";
import { setLeadConversionSettings } from "@/lib/leadConversions";
import { useVoiceTranscript } from "@/hooks/voice/useVoiceTranscript";
import { ctaBorderRadius, resolveCtaColors } from "@/lib/ctaButtonStyle";
import { cn } from "@/lib/utils";
import GenieNotFound from "@/pages/Errors/GenieNotFound";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { WidgetCommunicationService } from "@/services/integrations/widget/WidgetCommunicationService";
import { AgentConfig } from "@/types/agent/agent";
import { InfoLinksConfig } from "@/types/agent/info-links";
import { AgentPageProps } from "@/types/agent/agent-page";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { hasAskAlreadyBeenSent, markAskAsSent, parseAskParam } from "../live-agent/askParam";
import EmbedLiveAgentLayout, { EMBED_WIDE_LAYOUT_BREAKPOINT_PX } from "../live-agent/EmbedLiveAgentLayout";
import { useMediaQuery } from "@/hooks/ui";
import { CallStage } from "../live-agent/components/CallStage";
import { EmailStage } from "../live-agent/components/EmailStage";
import { HumanCallAvatar } from "../live-agent/components/HumanCallAvatar";
import { HumanCallBar } from "../live-agent/components/HumanCallBar";
import { HumanHandoffPrompt } from "../live-agent/components/HumanHandoffPrompt";
import { InlineCaptureMoment } from "../live-agent/components/InlineCaptureMoment";
import { ResourcesSheet } from "../live-agent/components/ResourcesSheet";
import { MoreActionsPopover } from "../live-agent/components/MoreActionsPopover";
import { VoiceSessionActionRow } from "../live-agent/components/VoiceSessionActionRow";
import { useRailItems } from "@/hooks/agent/useRailItems";
import { useSuggestedResourceMessages } from "@/hooks/agent/useSuggestedResourceMessages";
import { hasInChatMedia } from "@/lib/agent/mediaSources";
import type { TranscriptMessage } from "../live-agent/components/TranscriptPanel";
import { GenieSharedMedia } from "../live-agent/components/GenieAttachmentCard";
import { useFoundMedia } from "@/hooks/voice/useFoundMedia";
import { useLatestAttachments } from "@/hooks/voice/useLatestAttachments";
import { useMediaLookupPending } from "@/hooks/voice/useMediaLookupPending";

/**
 * Transcript entry. `imageAttachments` is optional and only set on the bubble
 * we push when a visitor sends photos — GenieTextChatOverlayWeb reads it to
 * render thumbnails inline. `attachment` is a link/picture the Genie put on
 * screen (showRelatedMedia).
 */
type EmbedTranscriptMessage = TranscriptMessage;

interface EmbedLiveAgentExperienceProps {
  agentPageData?: AgentPageProps["agentPageData"];
}

/**
 * Self-contained widget embed experience.
 *
 * Lives in its own file (extracted out of the 1700-line LiveAgentPage) so
 * widget bugs can be reasoned about without dragging desktop/mobile/portal/
 * demo logic into context. Owns its own voice agent, message state, and
 * handlers — nothing here knows about isMobile, chatExpanded, admin buttons,
 * lead-capture modals, or anything else that doesn't ship in the popup.
 *
 * Entry point: LiveAgentPage detects an embedded widget before mounting the
 * full-page experience, so only this component's hooks and services run.
 */
export const EmbedLiveAgentExperience = ({
  agentPageData: propAgentData,
}: EmbedLiveAgentExperienceProps = {}) => {
  const { url_name } = useParams<{ url_name: string }>();
  const [searchParams] = useSearchParams();
  const chatOnly = searchParams.get("mode") === "chat";
  const isSmallPopup = searchParams.get("popupSize") === "small";
  const { data: fetchedAgentResult, isLoading: isFetching } = useAgentPageByUrlNameFull(url_name);
  const [agent, setAgent] = useState<AgentPageProps["agentPageData"] | null>(propAgentData ?? null);
  const [error, setError] = useState<string | null>(null);

  // Which entry channel is active — voice/chat is the default; phone/email
  // swap the avatar area for CallStage/EmailStage, matching Studio's own
  // channel switcher and its More popover's "Call the Genie" / "Continue
  // over email" items.
  const [channel, setChannel] = useState<"voice" | "phone" | "email">("voice");
  const channelRequests = useChannelRequests({ agentId: agent?.id, urlName: agent?.url_name ?? url_name });

  // A prior conversation on this same browser, carried across page
  // navigations by widget.js (parent-page localStorage keyed by agentUrl —
  // the iframe's own conversationId doesn't survive a fresh page load).
  // Read once on mount: this seeds both the visible transcript and the
  // system-context recap fed into the new ElevenLabs session below, so the
  // agent doesn't greet a returning visitor as a stranger. It can't resume
  // the actual live voice session — the WebSocket dies on unload — so this
  // is a "pick up where we left off" illusion, not literal continuity.
  const [resumedTranscript] = useState(() => {
    const resumeId = searchParams.get("resumeConversationId");
    if (!resumeId) return null;
    return TranscriptStorageService.getStoredTranscript(resumeId);
  });
  const hasResumedTranscript = !!resumedTranscript && resumedTranscript.messages.length > 0;

  const priorConversationSummary = useMemo(() => {
    if (!hasResumedTranscript) return null;
    const RECAP_MAX_MESSAGES = 12;
    const recentMessages = resumedTranscript!.messages.slice(-RECAP_MAX_MESSAGES);
    const lines = recentMessages
      .filter((msg) => !msg.attachment)
      .map((msg) => `${msg.isUser ? "Customer" : "Agent"}: ${msg.message}`);
    return (
      "The visitor was already talking with you before navigating to a new page on this site. " +
      "Their voice/chat session was interrupted by the page change, but this is a continuation " +
      "of the same visit — do not greet them as a first-time visitor or restart the conversation " +
      "from scratch. Here is the recent conversation so far:\n\n" +
      lines.join("\n")
    );
  }, [hasResumedTranscript, resumedTranscript]);

  const [messages, setMessages] = useState<EmbedTranscriptMessage[]>(
    hasResumedTranscript
      ? resumedTranscript!.messages.map((msg) => ({
          message: msg.message,
          isUser: msg.isUser,
          timestamp: msg.timestamp,
          attachment: msg.attachment,
        }))
      : [],
  );
  const [showTranscript, setShowTranscript] = useState(hasResumedTranscript);
  const [showResources, setShowResources] = useState(false);
  const [conversationStartTime, setConversationStartTime] = useState<number | null>(null);
  const conversationStartTimeRef = useRef<number | null>(null);
  const {
    attachments,
    addFiles,
    removeAttachment,
    updateAttachmentAnalysis,
  } = useImageAttachments();

  // Prior-conversation recap takes priority over the agent's own userMemory
  // context (both prepend into the same [SYSTEM CONTEXT] block sent on
  // connect — see VoiceAgentService.sendInitialSystemContext) since it's more
  // specific to what's happening in this exact browsing session.
  const combinedUserMemory = priorConversationSummary
    ? agent?.userMemory
      ? `${priorConversationSummary}\n\n${agent.userMemory}`
      : priorConversationSummary
    : agent?.userMemory;

  const voiceAgent = useVoiceAgentConversation(agent?.elevenlabs_id, {
    welcomeMessage: agent?.welcome_message || agent?.page?.welcome_message || "Click to start",
    agentData: propAgentData || agent,
    ...(combinedUserMemory && { userMemory: combinedUserMemory }),
    onConversationEnd: () => {
      setShowTranscript(true);
      setConversationStartTime(null);
      conversationStartTimeRef.current = null;
    },
  });

  // Split audio controls (mic / agent voice / phone-style pause hold) — the
  // same hook the standalone live-agent page uses. Handler aliases keep the
  // existing JSX prop names working unchanged.
  const {
    isMicMuted,
    setIsMicMuted,
    isAgentVoiceMuted,
    setIsAgentVoiceMuted,
    isPaused,
    isAllAudioMuted,
    toggleMicMute: handleToggleMicMute,
    toggleAgentVoice: handleToggleAgentVoice,
    togglePause: handleTogglePause,
    toggleAllAudioMute: handleToggleAllAudioMute,
  } = useAudioSessionControls({ voiceAgent });

  // Live voice handoff to a human, via Twilio Voice JS SDK — fully additive,
  // dormant unless agent.voice_bridge_enabled is set (default false). Mirrors
  // the same flow in LiveAgentPage: AI client tool + manual button both call
  // confirmHandoff. No deferred-end callback — the embed has no wrap-up /
  // forms / redirect flow to hold back.
  const {
    voiceBridge,
    showPrompt: showHandoffPrompt,
    openPrompt: openHandoffPrompt,
    closePrompt: closeHandoffPrompt,
    error: handoffError,
    showHandoffButton: showHumanHandoffButton,
    confirmHandoff: handleConfirmHumanHandoff,
    hangupHandoff: handleHangupHumanHandoff,
  } = useHumanHandoff({
    voiceAgent,
    agentId: agent?.id,
    enabled: Boolean(agent?.voice_bridge_enabled),
    showButton: agent?.voice_bridge_show_button !== false,
  });
  const isVoiceBridgeEnabled = Boolean(agent?.voice_bridge_enabled);

  // Persist captured images against the conversation record so they reach the
  // conversation detail gallery and the report email / Slack / WhatsApp senders.
  // userId stays null here: widget visitors are anonymous, and the service
  // resolves a real id from the session when one happens to exist.
  const conversationId = voiceAgent.getConversationId();
  const {
    stageImages,
    isSaving: isSavingImages,
    pendingCount: pendingImageUploads,
    failedCount: failedImageUploads,
    retryFailed: retryFailedImageUploads,
    settleUploads: settleImageUploads,
  } = useConversationImageMediaSaver({
    conversationId,
    userId: null,
  });

  const endConversationAfterUploads = useCallback(async () => {
    await settleImageUploads(10_000);
    await voiceAgent.endConversation();
  }, [settleImageUploads, voiceAgent]);

  // Text chat: composer value, text-only/started-from-text mode, the
  // optimistic send flow, and ending a text chat. The embed silences both
  // audio channels when a visitor opts into typing (muteOnTextStart).
  const {
    inlineMessage,
    setInlineMessage,
    setIsTextOnlyMode,
    startedFromText,
    setStartedFromText,
    isTextInitiatedRef,
    optimisticUserMessagesRef,
    sendInlineMessage: handleInlineSend,
    endTextChat: handleEndTextChat,
    beginTextSession,
  } = useTextChatSession({
    voiceAgent,
    audio: { setIsMicMuted, setIsAgentVoiceMuted },
    attachments,
    removeAttachment,
    stageImages,
    endConversationAfterUploads,
    setMessages,
    setShowTranscript,
    setConversationStartTime,
    clientEvents: (
      agent?.settings as
        | { conversation_config?: { conversation?: { client_events?: string[] } } }
        | undefined
    )?.conversation_config?.conversation?.client_events,
    muteOnTextStart: true,
  });

  // Keep the ref in lockstep with the state — the text-chat hook above only
  // sets state, while the transcript-storage path below reads the ref.
  useEffect(() => {
    conversationStartTimeRef.current = conversationStartTime;
  }, [conversationStartTime]);

  // Hydrate agent data from the cached fetch.
  useEffect(() => {
    if (propAgentData) {
      setAgent(propAgentData);
      return;
    }
    if (isFetching) return;
    if (!fetchedAgentResult) {
      setError("Agent not found");
      return;
    }
    if (fetchedAgentResult.status === "ok") {
      setAgent(fetchedAgentResult.agent);
    } else if (fetchedAgentResult.status === "private") {
      // Private genies aren't reachable from a public widget; surface a not-found state.
      setError("Agent not found");
    } else {
      setError("Agent not found");
    }
  }, [propAgentData, fetchedAgentResult, isFetching]);

  // Lock html/body to 100% so the widget popup fills the iframe and the
  // pinned footer in EmbedLiveAgentLayout actually pins to the bottom.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.height = "100%";
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      html.style.height = prev.htmlHeight;
      body.style.height = prev.bodyHeight;
    };
  }, []);

  // Notify parent widget when the iframe is ready and when a conversation starts.
  useEffect(() => {
    WidgetCommunicationService.sendToParent("helpgenie:ready", { agentUrl: url_name });
  }, [url_name]);

  useEffect(() => {
    if (voiceAgent.connected) {
      const conversationId = voiceAgent.getConversationId();
      WidgetCommunicationService.sendToParent("helpgenie:conversation:started", {
        agentId: agent?.elevenlabs_id,
        conversationId,
      });
    }
  }, [voiceAgent.connected, agent?.elevenlabs_id, voiceAgent]);

  // A popup wide enough for two columns shows the chat beside the avatar as
  // soon as the conversation is live, as the live agent pages do. The visitor
  // can still close it from the action row.
  const isWidePopup = useMediaQuery(`(min-width: ${EMBED_WIDE_LAYOUT_BREAKPOINT_PX}px)`);
  useEffect(() => {
    if (voiceAgent.connected && isWidePopup) setShowTranscript(true);
  }, [voiceAgent.connected, isWidePopup]);

  // Auto-resume: once the agent has loaded, immediately reconnect in
  // text-only mode instead of waiting for the visitor to tap "Start
  // Conversation" again. Deliberately NOT voice — auto-requesting the
  // microphone with no user gesture on every page navigation is blocked by
  // most browsers anyway and is a bad (borderline dark-pattern) UX even when
  // allowed. Voice stays an explicit opt-in via the normal controls. The
  // prior transcript is already shown (seeded into `messages` above) and the
  // new session's system context carries the recap (via `combinedUserMemory`),
  // so continuing to type reads as "picking the conversation back up" even
  // though it's technically a fresh ElevenLabs session. Only fires once per mount.
  const hasAutoResumedRef = useRef(false);
  useEffect(() => {
    if (!hasResumedTranscript) return;
    if (hasAutoResumedRef.current) return;
    if (!agent?.elevenlabs_id) return;
    if (voiceAgent.connected || voiceAgent.isConnecting) return;
    hasAutoResumedRef.current = true;
    const service = voiceAgent.getService();
    if (service) {
      service.setOverrides({
        conversation: { textOnly: true },
        turn: { turn_timeout: 600, silence_end_call_timeout: 600 },
      });
    }
    const now = Date.now();
    setConversationStartTime(now);
    conversationStartTimeRef.current = now;
    setIsTextOnlyMode(true);
    setStartedFromText(true);
    isTextInitiatedRef.current = true;
    void voiceAgent.startConversation().then((success) => {
      if (success) {
        // Resume is a text session — silence both channels to match the
        // muteOnTextStart behavior of a normal typed start.
        const svc = voiceAgent.getService();
        if (svc) {
          void svc.setMicMuted(true);
          void svc.setVolume(0);
        }
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
      }
    });
  }, [hasResumedTranscript, agent?.elevenlabs_id, voiceAgent]);

  // Seeded first question (`?ask=`, MAXX's suggested-question deep link) —
  // skips the Talk/Chat chooser and sends the question straight into a text
  // session, same as the standalone live-agent page. Voice is never started.
  const askParam = useMemo(() => parseAskParam(searchParams.get("ask")), [searchParams]);
  const seededAskRef = useRef(false);
  useEffect(() => {
    if (!askParam || seededAskRef.current || !agent) return;
    if (!voiceAgent.getService()) return;

    const resolvedUrlName = agent.url_name ?? url_name ?? "";
    // Guards a plain reload of the same MAXX link from re-sending the same
    // question into a brand-new conversation.
    if (hasAskAlreadyBeenSent(resolvedUrlName, askParam)) {
      seededAskRef.current = true;
      return;
    }

    seededAskRef.current = true;
    markAskAsSent(resolvedUrlName, askParam);
    setShowTranscript(true);
    void handleInlineSend(askParam);
    // handleInlineSend is recreated every render; the ref guard is what makes
    // this run once, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, askParam, voiceAgent, url_name]);

  // Transcript: subscribe to the voice message stream, appending each entry
  // (skipping the optimistic-echo of typed messages) and persisting to
  // localStorage keyed by conversation id. Shared with the live-agent page.
  useVoiceTranscript({
    voiceAgent,
    setMessages,
    optimisticUserMessagesRef,
    conversationStartTimeRef,
  });
  const latestShare = useLatestAttachments(messages);
  const mediaPending = useMediaLookupPending();
  const hasFoundMedia = useFoundMedia().length > 0;

  const brand = agent?.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

  const handleAvatarClick = useCallback(async () => {
    if (!voiceAgent.connected && !voiceAgent.isConnecting) {
      const now = Date.now();
      setConversationStartTime(now);
      conversationStartTimeRef.current = now;
      setMessages([]);
    }
    await voiceAgent.toggleConversation();
  }, [voiceAgent]);

  // Exit chat → returns the visitor to the avatar / Start Conversation screen.
  // Triggered by the back-arrow button in the gradient header. Only ends the
  // conversation when it was actually a text-initiated session — if the
  // visitor just tapped "Type" to peek at the transcript during a live voice
  // call, Back should close the transcript panel and let the voice call
  // keep running underneath, not hang up on them.
  const handleExitChat = useCallback(async () => {
    if (startedFromText && (voiceAgent.connected || voiceAgent.isConnecting)) {
      try {
        await endConversationAfterUploads();
      } catch (e) {
        console.error("[EmbedLiveAgent] Error ending conversation on exit:", e);
      }
    }
    setShowTranscript(false);
    setInlineMessage("");
    if (startedFromText) {
      setStartedFromText(false);
      setIsTextOnlyMode(false);
      setIsMicMuted(false);
      setIsAgentVoiceMuted(false);
      setMessages([]);
      isTextInitiatedRef.current = false;
      conversationStartTimeRef.current = null;
    }
  }, [voiceAgent, startedFromText, endConversationAfterUploads]);

  // Suppressed during a live human call since HumanCallBar shows its own status.
  const getStatusMessage = useCallback(
    () => (isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" ? "" : voiceAgent.getStatusMessage()),
    [voiceAgent, isVoiceBridgeEnabled, voiceBridge.bridgeStatus],
  );

  // Suggested resources, shown in the chat as cards. Hooks have to sit above
  // the early returns below.
  // A genie that puts links in the chat itself needs none: its own cards are
  // the same links, shown better.
  const suggestedResources = useRailItems(
    hasInChatMedia(agent?.settings as AgentConfig | undefined)
      ? null
      : ((agent?.page?.info_links as InfoLinksConfig | null) ?? null),
  );
  const chatMessages = useSuggestedResourceMessages(messages, suggestedResources, voiceAgent.connected);

  // The widget asks for contact details only for genies counting chat leads as
  // ad conversions: the ask is what their requestContactDetails tool opens, and
  // every other genie's widget stays as it was.
  const leadConversions = agent?.branding?.embedStyle?.leadConversions;
  useEffect(() => {
    setLeadConversionSettings(leadConversions);
  }, [leadConversions]);

  const inConversationCapture = useInConversationCapture({
    connected: voiceAgent.connected && leadConversions?.enabled === true,
    messages,
    conversationStartTime,
    agentId: agent?.id,
    agentUserId: agent?.user_id,
    agentBusy: voiceAgent.speaking || voiceAgent.thinking || voiceAgent.processing || voiceAgent.searching || voiceAgent.retrieving,
    conversationId,
  });

  const { requestContactDetails } = inConversationCapture;
  const { onContactDetailsRequested } = voiceAgent;
  useEffect(() => {
    onContactDetailsRequested((prefill) => {
      const result = requestContactDetails(prefill);
      // The ask sits in the chat, so a voice visitor needs it opened to see it.
      if (result === "shown") setShowTranscript(true);
      return result;
    });
    return () => onContactDetailsRequested(null);
  }, [onContactDetailsRequested, requestContactDetails]);

  if (error || (!isFetching && !agent && !propAgentData)) {
    return <GenieNotFound isInactive={false} />;
  }

  if (!agent) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div className="text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  // Chat is "open" when the session started as text OR the panel was opened.
  // Typing during a voice call flips isTextOnlyMode but the call stays a voice
  // call, so only startedFromText counts as a text session here.
  const chatOpen = chatOnly || showTranscript || startedFromText;

  const resourcesConfig = (agent?.page?.info_links as InfoLinksConfig | null) ?? null;
  const hasResources = !!(
    resourcesConfig &&
    ((resourcesConfig.documents?.length ?? 0) > 0 || (resourcesConfig.links?.length ?? 0) > 0)
  ) || hasFoundMedia;

  /**
   * The embed owns its own hidden inputs. It used to click
   * `#genie-chat-attachments`, which only ever renders in LiveAgentPage — and
   * this component is an early return from that page, so the element was never
   * in the tree. The attach button in the embed did nothing at all.
   */
  const openEmbedPicker = (source: "library" | "camera") => {
    const id = source === "camera" ? "genie-embed-camera" : "genie-embed-attachments";
    (document.getElementById(id) as HTMLInputElement | null)?.click();
  };

  const embedStyle = agent?.branding?.embedStyle;
  const actionButtonStyle = embedStyle?.actionButtons;
  const widgetBackground = embedStyle?.background;
  const phoneNumber = agent?.phone?.number || null;
  const mailboxAddress = agent?.mailbox?.status === "provisioned" ? agent.mailbox.email_address : null;
  const channelPageUrl = `${window.location.origin}/live-agent/${url_name}`;

  const inlineCaptureNode = inConversationCapture.visible ? (
    <InlineCaptureMoment
      brandColor={brand}
      secondaryColor={agent?.branding?.secondaryColor}
      agentName={agent?.name}
      resourceLabel={inConversationCapture.resourceLabel}
      contactRequest={inConversationCapture.contactRequested}
      values={inConversationCapture.capture.values}
      onChange={inConversationCapture.capture.onChange}
      onSubmit={inConversationCapture.capture.submit}
      isSubmitting={inConversationCapture.capture.isSubmitting}
      submitted={inConversationCapture.capture.submitted}
      error={inConversationCapture.capture.error}
      onDismiss={inConversationCapture.dismiss}
      alreadyCapturedEmail={
        inConversationCapture.capture.alreadyCaptured && !inConversationCapture.capture.editing
          ? inConversationCapture.capture.capturedEmail
          : null
      }
      onChangeEmail={inConversationCapture.capture.startEdit}
    />
  ) : null;

  // Idle Talk/Chat button styling — both are CTA-class buttons, each with its
  // own independently configurable color set (embedStyle.ctaButton /
  // .chatButton), sharing the popup's existing button-shape setting
  // (embedStyle.buttonStyle) — the same field the old single Start
  // Conversation CTA used. Only applied inline when explicitly set, so the
  // default stays the existing Tailwind rounded-full pill (an inline 24px
  // radius is close but not visually identical to true rounded-full at this
  // button height).
  const idleCta = resolveCtaColors({
    brandColor: brand,
    gradientEnd: agent?.branding?.secondaryColor || DEFAULT_BRANDING.secondaryColor,
    connected: false,
    gradient: true,
    colorOverride: embedStyle?.ctaButton?.color,
    colorOverride2: embedStyle?.ctaButton?.color2,
    textColorOverride: embedStyle?.ctaButton?.textColor,
  });
  const idleCtaRadius = embedStyle?.buttonStyle ? ctaBorderRadius(embedStyle.buttonStyle) : undefined;
  const idleCtaScale = embedStyle?.ctaButton?.sizeScale ?? 1;
  const chatButtonScale = embedStyle?.chatButton?.sizeScale ?? 1;

  // Action row varies by state. Pre-conversation, the new Talk/Chat buttons
  // (rendered below, inside the layout's children) fully own starting voice
  // or text — so VoiceSessionActionRow is hidden until connected, exactly
  // like LiveAgentPage's own `hidden={minimalVoiceStart}` pattern. Once
  // connected, passing onEndConversation switches it into its own
  // already-built Mic/Stop/More pill — same one Studio uses. isTranscriptFeatureEnabled
  // still needs to stay true so "Show chat" exists as a fallback inside More,
  // even though the widget also gets its own dedicated Chat button below —
  // "Show chat" sat one level deep in that menu and visitors weren't finding it.
  // Human handoff is a More-menu action, as on the live agent pages — never a
  // button of its own on the stage.
  const onTalkToHuman =
    showHumanHandoffButton && voiceBridge.bridgeStatus === "idle" ? openHandoffPrompt : undefined;

  const compactVoiceControls = (
    <div className="-my-1 flex items-center gap-2 [&>div]:!mt-0 [&>div]:!px-0 [&>div>div]:!gap-2 [&_span[class*='max-w-\\[5\\.5rem\\]']]:!hidden">
      <VoiceSessionActionRow
        hidden={!voiceAgent.connected}
        brandColor={brand}
        isMobile={true}
        isTranscriptFeatureEnabled
        voiceConnected={voiceAgent.connected}
        onEndConversation={() => void handleAvatarClick()}
        isTextOnlyMode={startedFromText}
        isMicMuted={isMicMuted}
        onToggleMicMute={() => void handleToggleMicMute()}
        isAgentVoiceMuted={isAgentVoiceMuted}
        onToggleAgentVoice={() => void handleToggleAgentVoice()}
        isPaused={isPaused}
        onTogglePause={() => void handleTogglePause()}
        onOpenChat={() => setShowTranscript(!showTranscript)}
        chatOpen={showTranscript}
        hasResources={hasResources}
        onOpenResources={() => setShowResources(true)}
        infoTitle={`About ${agent?.name || "this Genie"}`}
        infoDescription={agent?.description || null}
        infoLinks={agent?.page?.info_links as InfoLinksConfig | null}
        qrLogoUrl={agent?.branding?.logoUrl || null}
        onAddImage={openEmbedPicker}
        onCallGenie={phoneNumber ? () => setChannel("phone") : undefined}
        onEmailGenie={mailboxAddress ? () => setChannel("email") : undefined}
        emailActionLabel="Continue over email"
        onTalkToHuman={onTalkToHuman}
        actionButtonStyle={actionButtonStyle}
      />
    </div>
  );

  // This is the row that actually renders while connected-but-not-chatting —
  // EmbedLiveAgentLayout only shows headerVoiceControls once chatOpen is
  // already true, so the always-visible pill during a plain voice call comes
  // from here (via `actionRow`), not from compactVoiceControls above.
  //
  // Widget-only 4-button pill (Mic / Chat / Stop / More) — built locally
  // instead of via VoiceSessionActionRow's connected branch (which only ever
  // renders 3: Mic/Stop/More) so the dedicated "open transcript" button sits
  // inside the same pill rather than floating beside it. "Show chat" also
  // stays available one level deep inside More as a fallback.
  const startScreenActionRow = !voiceAgent.connected ? null : (
    <div className="mt-6 flex w-full justify-center px-3">
      <div
        role="group"
        aria-label="Conversation controls"
        className="flex items-center gap-2 rounded-full p-1.5"
        style={{ background: `${brand}0d` }}
      >
        <button
          type="button"
          onClick={() => void handleToggleMicMute()}
          aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
          aria-pressed={isMicMuted}
          title={isMicMuted ? "Unmute microphone" : "Mute microphone"}
          className="flex h-11 w-11 items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
          style={{ color: isMicMuted ? "#FFFFFF" : brand, background: isMicMuted ? brand : "transparent" }}
        >
          {isMicMuted ? <MicOff size={20} aria-hidden="true" /> : <Mic size={20} aria-hidden="true" />}
        </button>
        <button
          type="button"
          onClick={() => setShowTranscript(!showTranscript)}
          aria-label={showTranscript ? "Hide chat" : "Show chat"}
          aria-pressed={showTranscript}
          title={showTranscript ? "Hide chat" : "Show chat"}
          className="flex h-11 w-11 items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
          style={{ color: showTranscript ? "#FFFFFF" : brand, background: showTranscript ? brand : "transparent" }}
        >
          <MessageSquare size={20} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => void handleAvatarClick()}
          aria-label="End conversation"
          title="End conversation"
          className="flex h-11 w-14 items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
          style={{
            color: brand,
            background: `linear-gradient(135deg, ${brand}20, ${agent?.branding?.secondaryColor || brand}14)`,
          }}
        >
          <Square size={17} fill="currentColor" aria-hidden="true" />
        </button>
        <MoreActionsPopover
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          size={44}
          hideLabel
          onToggleAgentVoice={() => void handleToggleAgentVoice()}
          isAgentVoiceMuted={isAgentVoiceMuted}
          onTogglePause={() => void handleTogglePause()}
          isPaused={isPaused}
          onAddImage={openEmbedPicker}
          onOpenResources={hasResources ? () => setShowResources(true) : undefined}
          onTalkToHuman={onTalkToHuman}
          onCallGenie={phoneNumber ? () => setChannel("phone") : undefined}
          onEmailGenie={mailboxAddress ? () => setChannel("email") : undefined}
          emailActionLabel="Continue over email"
          actionButtonStyle={{ ...actionButtonStyle, idleBackgroundColor: "transparent" }}
        />
      </div>
    </div>
  );

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] w-full flex-col overflow-hidden bg-transparent">
      <EmbedLiveAgentLayout
        chatOnly={chatOnly}
        chatOpen={chatOpen}
        brandColor={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        agentName={agent?.name}
        agentDescription={agent?.description || null}
        phoneNumber={phoneNumber}
        hideWelcomeText={embedStyle?.widgetHideWelcomeText ?? false}
        customAvatarUrl={agent?.branding?.logoUrl || null}
        backgroundColor={embedStyle?.widgetBackgroundColor}
        background={widgetBackground}
        headerLogoSize={embedStyle?.headerLogoSize}
        headerStyle={embedStyle?.headerStyle}
        chatStyle={embedStyle?.chatStyle}
        actionButtonStyle={actionButtonStyle}
        hideAttribution={agent?.branding?.hideAttribution}
        urlName={url_name}
        messages={chatMessages}
        isConnected={voiceAgent.connected}
        isConnecting={voiceAgent.isConnecting}
        isSpeaking={voiceAgent.speaking}
        isAllAudioMuted={isAllAudioMuted}
        onToggleAllAudioMute={() => void handleToggleAllAudioMute()}
        isTextOnlyMode={startedFromText}
        inlineMessage={inlineMessage}
        onChangeInlineMessage={setInlineMessage}
        onSend={() => void handleInlineSend()}
        startedFromText={startedFromText}
        onCloseChat={() => setShowTranscript(false)}
        onEndChat={() => void handleEndTextChat()}
        onEndConversation={() => void (chatOnly ? handleEndTextChat() : endConversationAfterUploads())}
        onExitChat={() => void handleExitChat()}
        attachments={attachments.map((att) => ({
          id: att.id,
          previewUrl: att.previewUrl,
          status: att.status,
          analysisText: att.analysisText,
        }))}
        onRemoveAttachment={removeAttachment}
        onOpenFilePicker={() => openEmbedPicker("library")}
        onUpdateAttachmentAnalysis={updateAttachmentAnalysis}
        imageUploadPendingCount={pendingImageUploads}
        imageUploadFailedCount={failedImageUploads}
        imageUploadSaving={isSavingImages}
        onRetryImageUploads={retryFailedImageUploads}
        headerVoiceControls={chatOnly ? undefined : compactVoiceControls}
        inlineCaptureSlot={inlineCaptureNode}
        actionRow={channel === "voice" ? startScreenActionRow : undefined}
      >
        {channel === "phone" ? (
          <CallStage
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            phoneNumber={phoneNumber}
            callbackAvailable={agent?.outbound_connected === true}
            onRequestCallback={channelRequests.requestCallback}
            pageUrl={channelPageUrl}
          />
        ) : channel === "email" ? (
          <EmailStage
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            onSubmit={channelRequests.askQuestion}
            pageUrl={channelPageUrl}
          />
        ) : (
          <>
            {!(embedStyle?.hideWelcomeMessage && !voiceAgent.connected) && (
              <p
                className={cn(
                  "text-center font-medium leading-[1.4] text-[#666] px-4 py-2 bg-transparent border-none rounded-xl",
                  isSmallPopup ? "text-[0.8rem] mb-3 max-w-[240px]" : "text-[0.95rem] mb-6 max-w-[280px]",
                )}
              >
                {getStatusMessage()}
              </p>
            )}

            {isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" ? (
              <HumanCallAvatar
                brandColor={brand}
                status={voiceBridge.bridgeStatus}
                size={isSmallPopup ? 150 : 200}
                embedded
              />
            ) : (
              <VoiceAgentAvatar
                brandColor={brand}
                customBranding={agent?.branding}
                coverImageUrl={embedStyle?.avatarImageUrl}
                showLogoOverlay={embedStyle?.avatarShowLogoOverlay}
                sizeScale={embedStyle?.avatarSizeScale}
                onClick={() => void handleAvatarClick()}
                speaking={voiceAgent.speaking}
                listening={voiceAgent.listening}
                thinking={voiceAgent.thinking}
                processing={voiceAgent.processing}
                searching={voiceAgent.searching}
                retrieving={voiceAgent.retrieving}
                isConnecting={voiceAgent.isConnecting}
                connected={voiceAgent.connected}
                size={isSmallPopup ? 150 : 200}
              />
            )}

            {/* Idle entry point — substituted for the old single "Start
                Conversation" button. Matches Studio's Talk/Chat pair; the
                connected Mic/Stop/More pill still comes from
                VoiceSessionActionRow above (onEndConversation), unchanged. */}
            {!voiceAgent.connected && (
              <div
                className={cn(
                  "flex w-full max-w-sm flex-wrap justify-center gap-3 px-4",
                  isSmallPopup ? "mt-3" : "mt-6",
                )}
                role="group"
                aria-label="Choose how to start your conversation"
              >
                <Button
                  type="button"
                  onClick={() => void handleAvatarClick()}
                  disabled={voiceAgent.isConnecting}
                  aria-busy={voiceAgent.isConnecting}
                  className="min-h-[44px] min-w-[120px] gap-2 rounded-full px-6 font-semibold shadow-lg transition-opacity hover:opacity-90"
                  style={{
                    background: idleCta.background,
                    color: idleCta.color,
                    borderRadius: idleCtaRadius,
                    ...(idleCtaScale !== 1 ? { transform: `scale(${idleCtaScale})` } : {}),
                  }}
                >
                  {voiceAgent.isConnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  ) : (
                    <Mic className="h-4 w-4" aria-hidden="true" />
                  )}
                  {voiceAgent.isConnecting ? "Connecting…" : "Talk"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void beginTextSession()}
                  disabled={voiceAgent.isConnecting}
                  className="min-h-[44px] min-w-[120px] gap-2 rounded-full px-6 font-semibold"
                  style={{
                    borderColor: embedStyle?.chatButton?.color || undefined,
                    color: embedStyle?.chatButton?.color || undefined,
                    backgroundColor: embedStyle?.chatButton?.backgroundColor || undefined,
                    borderRadius: idleCtaRadius,
                    ...(chatButtonScale !== 1 ? { transform: `scale(${chatButtonScale})` } : {}),
                  }}
                >
                  <MessageSquare className="h-4 w-4" aria-hidden="true" />
                  Chat
                </Button>
              </div>
            )}

            {voiceAgent.connected && !chatOpen && (latestShare.attachments.length > 0 || mediaPending) && (
              <div className={cn("flex w-full justify-center px-3", isSmallPopup ? "mt-3" : "mt-4")}>
                <GenieSharedMedia
                  attachments={latestShare.attachments}
                  brandColor={brand}
                  agentName={agent?.name}
                  agentId={agent?.id}
                  onDismiss={latestShare.dismiss}
                  pending={mediaPending}
                  className="max-w-[320px]"
                />
              </div>
            )}

            {isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" && (
              <HumanCallBar
                brandColor={brand}
                secondaryColor={agent?.branding?.secondaryColor}
                status={voiceBridge.bridgeStatus}
                muted={voiceBridge.muted}
                destinationLabel={voiceBridge.destinationLabel}
                onToggleMute={voiceBridge.toggleMute}
                onHangup={handleHangupHumanHandoff}
              />
            )}
          </>
        )}
      </EmbedLiveAgentLayout>

      {isVoiceBridgeEnabled && (
        <HumanHandoffPrompt
          open={showHandoffPrompt}
          onOpenChange={(open) => {
            if (open) openHandoffPrompt();
            else closeHandoffPrompt();
          }}
          brandColor={brand}
          agentName={agent?.name}
          isConnecting={voiceBridge.bridgeStatus === "requesting-token" || voiceBridge.bridgeStatus === "connecting"}
          error={handoffError}
          onConfirm={() => void handleConfirmHumanHandoff()}
        />
      )}

      <ResourcesSheet
        open={showResources}
        onClose={() => setShowResources(false)}
        brandColor={brand}
        resources={resourcesConfig}
      />

      {/* Hidden inputs for image attachments, scoped to the embed. Two, not one:
          setting `capture` imperatively then clicking in the same tick races
          React, and Safari caches the picker configuration on first open. */}
      <input
        id="genie-embed-attachments"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <input
        id="genie-embed-camera"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );
};

export default EmbedLiveAgentExperience;
